import { Minimize2 } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { isEditableKeyboardTarget } from "./photos.utils";

interface PhotoGalleryFullscreenProps {
  photoCount: number;
  isPreviewOpen: boolean;
  onClosePreview: () => void;
  onExit: () => void;
  previewOverlayRef: Ref<HTMLDivElement>;
  children: ReactNode;
}

// Not the Radix Dialog: its modal layer blocks the base-ui Combobox popups in the toolbar and label pickers.
export function PhotoGalleryFullscreen({
  photoCount,
  isPreviewOpen,
  onClosePreview,
  onExit,
  previewOverlayRef,
  children,
}: PhotoGalleryFullscreenProps) {
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    containerRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || isEditableKeyboardTarget(event.target)) {
        return;
      }
      event.preventDefault();
      if (isPreviewOpen) {
        onClosePreview();
      } else {
        onExit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPreviewOpen, onClosePreview, onExit]);

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-white outline-none"
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3">
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
          Photos <span className="ml-1 text-sm font-normal text-gray-500">{photoCount}</span>
        </h2>
        <Button variant="ghost" onClick={onExit}>
          <Minimize2 />
          Exit full screen
        </Button>
      </div>
      <div className="relative min-h-0 flex-1">
        <div className="h-full overflow-y-auto">{children}</div>
        <div ref={previewOverlayRef} className="pointer-events-none absolute inset-0 z-10" />
      </div>
    </div>,
    document.body,
  );
}
